import z from 'zod';
import { type PortfolioNamedEntry, useRepositoryMutation } from '@mews-commander/data-access';
import { type CommanderFormProps, useHandledDialogFormControls } from '@mews-commander/form';
import { createZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import { accountingConfigurationSchema } from '../../forms/schemas/configureProductFormSchema';
import { AccountingForm } from '../../ConfigureProductForm/steps/AccountingForm';
import type { LocalProductSidePanelFormProps } from '../types';

type AccountingProductFormValues = z.infer<typeof accountingConfigurationSchema>;

interface AccountingProductFormProps extends LocalProductSidePanelFormProps {
    accountingCategoryKey?: PortfolioNamedEntry;
    cancellationAccountingCategoryKey?: PortfolioNamedEntry;
    rebateAccountingCategoryKey?: PortfolioNamedEntry;
}

type Props = CommanderFormProps & AccountingProductFormProps;

export const EditProductAccountingForm = (props: Props) => {
    const {
        cancellationAccountingCategoryKey,
        accountingCategoryKey,
        rebateAccountingCategoryKey,
        propertyId,
        enterpriseProductId,
        centralProductId,
        ...formProps
    } = props;

    const { mutateAsync } = useRepositoryMutation('portfolioEnterpriseProducts', 'updateAccountingCategories', {
        onSuccess: () => showSuccessAlert({ title: lt('ChangesSaved') }),
        invalidateQueries: {
            portfolioEnterpriseProducts: ['getDetail'],
        },
    });

    const { renderFormContent } = useHandledDialogFormControls<AccountingProductFormValues>({
        ...formProps,
        onSubmit: async (values) => {
            await mutateAsync({
                centralProductId,
                propertyId,
                enterpriseProductId,
                accountingCategoryId: values.accountingCategoryKey,
                rebateAccountingCategoryId: values.rebateAccountingCategoryKey,
                cancellationAccountingCategoryId: values.cancellationAccountingCategoryKey,
            });
        },
        values: {
            cancellationAccountingCategoryKey: cancellationAccountingCategoryKey?.id || '',
            accountingCategoryKey: accountingCategoryKey?.id || '',
            rebateAccountingCategoryKey: rebateAccountingCategoryKey?.id || '',
        },
        resolver: createZodFormResolver(accountingConfigurationSchema),
    });

    return renderFormContent(<AccountingForm propertyId={propertyId} />);
};
